import React, { useEffect, useState } from "react";
import { Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import LabShell from "./LabShell";
import { Panel, EmptyState, formatRelative } from "./labUi";
import {
    fetchRuns,
    fetchRunRecommendations,
    submitInbox,
    isBackendUnavailable,
} from "../../services/LabService";
import { labHref, navigate } from "./labRouter";

/** No global recommendations endpoint exists; aggregate per-run instead. */
const aggregateRecommendations = async (limit = 10) => {
    const runs = await fetchRuns({ status: "SUCCEEDED", limit });
    const recsPerRun = await Promise.all(
        (runs.items || []).map(async (run) => {
            try {
                const res = await fetchRunRecommendations(run.run_id);
                return (res?.items || res || []).map((r) => ({
                    ...r,
                    source_run_id: run.run_id,
                    source_proposal_id: run.proposal_id,
                    source_completed_at: run.stopped_at,
                }));
            } catch (_) {
                return [];
            }
        })
    );
    return recsPerRun.flat();
};

const RecRow = ({ rec }) => {
    const [showYaml, setShowYaml] = useState(false);
    const [pending, setPending] = useState(false);
    const [queued, setQueued] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    if (dismissed) return null;

    const queue = async () => {
        setPending(true);
        try {
            await submitInbox({
                yamlContent: rec.yaml || rec.raw_yaml,
                proposalId: rec.proposal_id || rec.id,
            });
            setQueued(true);
        } catch (err) {
            alert(err.message);
        } finally {
            setPending(false);
        }
    };

    return (
        <div className="border-b border-slate-100 dark:border-slate-800 last:border-b-0 px-4 py-3">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <h3 className="text-sm font-medium">{rec.title || rec.proposal_id || rec.id}</h3>
                    {rec.source_run_id ? (
                        <a
                            href={labHref(`experiments/${rec.source_run_id}`)}
                            onClick={(e) => { e.preventDefault(); navigate(labHref(`experiments/${rec.source_run_id}`)); }}
                            className="text-xs text-indigo-600 hover:text-indigo-700"
                        >
                            From {rec.source_proposal_id || rec.source_run_id} · {formatRelative(rec.source_completed_at)}
                        </a>
                    ) : null}
                    {rec.rationale ? <p className="text-xs text-slate-500 mt-1">{rec.rationale}</p> : null}
                    {rec.yaml || rec.raw_yaml ? (
                        <button
                            onClick={() => setShowYaml((v) => !v)}
                            className="mt-2 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-700"
                        >
                            {showYaml ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                            {showYaml ? "Hide" : "Show"} YAML
                        </button>
                    ) : null}
                    {showYaml && (rec.yaml || rec.raw_yaml) ? (
                        <pre className="mt-2 p-3 rounded-md bg-slate-900 text-slate-100 text-[11px] overflow-auto max-h-72">{rec.yaml || rec.raw_yaml}</pre>
                    ) : null}
                </div>
                <div className="flex flex-col gap-1.5 flex-shrink-0">
                    <button
                        onClick={queue}
                        disabled={pending || queued || !(rec.yaml || rec.raw_yaml)}
                        className="text-xs px-2.5 py-1 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
                    >
                        {queued ? "Queued ✓" : pending ? "Queueing…" : "Queue"}
                    </button>
                    <button
                        onClick={() => setDismissed(true)}
                        className="text-xs px-2.5 py-1 rounded-md border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800"
                    >
                        Dismiss
                    </button>
                </div>
            </div>
        </div>
    );
};

const LabRecommendations = () => {
    const [data, setData] = useState({ loading: true, items: [], error: null });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const items = await aggregateRecommendations(10);
                if (!cancelled) setData({ loading: false, items, error: null });
            } catch (error) {
                if (isBackendUnavailable(error)) {
                    if (!cancelled) setData({ loading: false, items: [], error: null });
                } else if (!cancelled) {
                    setData({ loading: false, items: [], error });
                }
            }
        })();
        return () => { cancelled = true; };
    }, []);

    return (
        <LabShell activeKey="recommendations">
            <Panel
                title={
                    <span className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-amber-500" />
                        Pending recommendations
                    </span>
                }
                padded={false}
            >
                {data.loading ? (
                    <div className="px-4 py-8 text-center text-sm text-slate-500">Loading recommendations…</div>
                ) : data.error ? (
                    <div className="px-4 py-8 text-center text-sm text-red-600">{data.error.message}</div>
                ) : data.items.length === 0 ? (
                    <EmptyState
                        icon={<Sparkles className="w-6 h-6" />}
                        title="No pending recommendations"
                        body="Recommendations appear here after a run completes. Queue one to start a follow-up experiment."
                    />
                ) : (
                    <div>
                        {data.items.map((rec, i) => (
                            <RecRow key={`${rec.source_run_id}:${rec.proposal_id || rec.id || i}`} rec={rec} />
                        ))}
                    </div>
                )}
            </Panel>
        </LabShell>
    );
};

export default LabRecommendations;
